import React from 'react';
import ChatEmptyState from './ChatEmptyState';
import ChatMessageList from './ChatMessageList';
import OarsPracticePane from './oars/OarsPracticePane';
import SpecificStrategiesPracticePane from './strategies/SpecificStrategiesPracticePane';

function visibleStrategyAnnotations(strategiesFlow) {
  const results = strategiesFlow?.turnResults || [];
  const revealed = new Set(strategiesFlow?.revealedGuidanceTurnIndexes || []);
  const finalTurnIndex = results.length - 1;
  const annotations = [];
  const seen = new Set();

  results.forEach((result, turnIndex) => {
    const finalGuidanceVisible = strategiesFlow?.status === 'complete' && turnIndex === finalTurnIndex;
    const interimGuidanceRevealed = strategiesFlow?.interimGuidanceVisible && revealed.has(turnIndex);
    if (!finalGuidanceVisible && !interimGuidanceRevealed) return;

    (result?.evaluation?.stimulus_annotations || []).forEach((annotation) => {
      const key = [
        annotation.turn_id,
        annotation.start,
        annotation.end,
        annotation.explanation,
      ].join('|');
      if (seen.has(key)) return;
      seen.add(key);
      annotations.push(annotation);
    });
  });

  return annotations;
}

function ChatBody({
  t,
  theme = 'dark',
  isOarsMode,
  isStrategiesMode,
  isClientMode,
  humanSpeaker = 'counsellor',
  showWelcomePanel,
  showInlineModeSelector,
  hideEmptyStateTitle = false,
  hideEmptyStateDescription = false,
  singleColumnEmptyPrompt = '',
  activeMode,
  onFreeForm,
  onBeTheClient,
  onOars,
  onStrategies,
  oarsExercise,
  oarsExerciseLoading,
  strategiesFlow,
  oarsAttempt,
  isSpecificSkillReplay = false,
  oarsFilter,
  oarsPopupBlocking,
  oarsPendingUtterance,
  showRandomSkillChoicePanel,
  selectedRandomSkillType,
  onSelectRandomSkillType,
  showRandomSkillChoiceCue,
  skillsAnnotationsEnabled = true,
  suppressAnnotationTooltips = false,
  conversation,
  viewedTurnIndex,
  highlightedTurnIndex = null,
  totalTurns,
  fullSessionAnnotations = [],
  llmMode,
  llmPaused,
  llmFinishingUtterance,
  sessionEnded,
  isConsentPending,
  showBtcConsentCue,
  handleConsentChoice,
  userAvatarPreference,
  messagesContainerRef,
  messagesEndRef,
  turnAnchorRefs,
}) {
  if (isStrategiesMode) {
    return (
      <SpecificStrategiesPracticePane
        activeMode={activeMode}
        onFreeForm={onFreeForm}
        onBeTheClient={onBeTheClient}
        onOars={onOars}
        onStrategies={onStrategies}
        strategiesFlow={strategiesFlow}
        theme={theme}
        t={t}
        stimulusAnnotations={skillsAnnotationsEnabled
          ? visibleStrategyAnnotations(strategiesFlow)
          : []}
        suppressAnnotationTooltips={suppressAnnotationTooltips}
        showInlineModeSelector={showInlineModeSelector}
        hideEmptyStateTitle={hideEmptyStateTitle}
        hideEmptyStateDescription={hideEmptyStateDescription}
        singleColumnEmptyPrompt={singleColumnEmptyPrompt}
        userAvatarPreference={userAvatarPreference}
        messagesContainerRef={messagesContainerRef}
        messagesEndRef={messagesEndRef}
      />
    );
  }

  if (isOarsMode) {
    return (
      <OarsPracticePane
        t={t}
        theme={theme}
        activeMode={activeMode}
        onFreeForm={onFreeForm}
        onBeTheClient={onBeTheClient}
        onOars={onOars}
        onStrategies={onStrategies}
        showInlineModeSelector={showInlineModeSelector}
        hideEmptyStateTitle={hideEmptyStateTitle}
        hideEmptyStateDescription={hideEmptyStateDescription}
        singleColumnEmptyPrompt={singleColumnEmptyPrompt}
        oarsExercise={oarsExercise}
        oarsExerciseLoading={oarsExerciseLoading}
        replayUtterance={isSpecificSkillReplay ? oarsAttempt?.utterance : null}
        stimulusAnnotations={skillsAnnotationsEnabled ? (oarsAttempt?.evaluation?.stimulus_annotations || []) : []}
        oarsPopupBlocking={oarsPopupBlocking}
        oarsPendingUtterance={oarsPendingUtterance}
        showRandomSkillChoicePanel={showRandomSkillChoicePanel}
        selectedRandomSkillType={selectedRandomSkillType}
        onSelectRandomSkillType={onSelectRandomSkillType}
        showRandomSkillChoiceCue={showRandomSkillChoiceCue}
        suppressAnnotationTooltips={suppressAnnotationTooltips}
        userAvatarPreference={userAvatarPreference}
        messagesContainerRef={messagesContainerRef}
        messagesEndRef={messagesEndRef}
      />
    );
  }

  if (showWelcomePanel) {
    return (
      <ChatEmptyState
        t={t}
        theme={theme}
        variant={isClientMode ? 'client-mode' : 'selected'}
        showModeSelector={showInlineModeSelector}
        hideTitle={hideEmptyStateTitle}
        hideDescription={hideEmptyStateDescription}
        singleColumnEmptyPrompt={singleColumnEmptyPrompt}
        activeMode={activeMode}
        onFreeForm={onFreeForm}
        onBeTheClient={onBeTheClient}
        onOars={onOars}
        onStrategies={onStrategies}
      />
    );
  }

  return (
    <ChatMessageList
      conversation={conversation}
      isClientMode={isClientMode}
      humanSpeaker={humanSpeaker}
      viewedTurnIndex={viewedTurnIndex}
      highlightedTurnIndex={highlightedTurnIndex}
      totalTurns={totalTurns}
      fullSessionAnnotations={fullSessionAnnotations}
      skillsAnnotationsEnabled={skillsAnnotationsEnabled}
      suppressAnnotationTooltips={suppressAnnotationTooltips}
      theme={theme}
      t={t}
      llmMode={llmMode}
      llmPaused={llmPaused}
      llmFinishingUtterance={llmFinishingUtterance}
      sessionEnded={sessionEnded}
      isConsentPending={isConsentPending}
      showBtcConsentCue={showBtcConsentCue}
      handleConsentChoice={handleConsentChoice}
      userAvatarPreference={userAvatarPreference}
      messagesContainerRef={messagesContainerRef}
      messagesEndRef={messagesEndRef}
      turnAnchorRefs={turnAnchorRefs}
    />
  );
}

export default ChatBody;
